import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCloset, ClosetItem } from './useCloset';
import { useWeather } from './useWeather';
import { useFeed } from './useFeed';
import { useAuth } from './useAuth';
import { useAgentMemory } from './useAgentMemory';
import { toast } from 'sonner';

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: AgentAction[];
  display?: 'outfit' | 'wardrobe' | 'calendar' | 'trip' | 'chat' | 'camera';
  data?: any;
  isLoading?: boolean;
  isStreaming?: boolean;
  interactionId?: string | null;
}

export interface AgentAction {
  type: string;
  label: string;
  params?: Record<string, any>;
}

export interface AgentContext {
  wardrobe: ClosetItem[];
  weather: any;
  preferences: any;
  profile: any;
}

export function useAuraAgent() {
  const [messages, setMessages] = useState<AgentMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Welcome to AURA. I'm your personal style intelligence. How can I elevate your look today?",
      timestamp: new Date(),
      actions: [
        { type: 'generate_outfit', label: "Style me for today" },
        { type: 'open_camera', label: "Add to archive" },
        { type: 'browse_wardrobe', label: "View wardrobe" },
      ],
    },
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { items, addItem, deleteItem, uploadImage } = useCloset();
  const { weather } = useWeather();
  const { profile } = useAuth();
  const { createPost } = useFeed();
  const { logInteraction, getSessionId } = useAgentMemory();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const addMessage = useCallback((message: Omit<AgentMessage, 'id' | 'timestamp'>) => {
    const newMessage: AgentMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<AgentMessage>) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  // Tool: Generate Outfit
  const generateOutfit = useCallback(async (context?: { occasion?: string; weather?: any }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please sign in to continue');

      const response = await supabase.functions.invoke('ai-stylist', {
        body: {
          items: items.map(i => ({
            id: i.id,
            name: i.name,
            type: i.type,
            color: i.color,
            occasion: i.occasion,
            mood: i.mood,
            imageUrl: i.image_url,
          })),
          weather: context?.weather || weather,
          occasion: context?.occasion || 'daily',
          stylePreferences: profile?.style_preferences,
        },
      });

      if (response.error) throw response.error;

      const outfitData = response.data;
      const mappedOutfit = {
        ...outfitData,
        top: items.find(i => i.id === outfitData.topId) || null,
        bottom: items.find(i => i.id === outfitData.bottomId) || null,
        shoes: items.find(i => i.id === outfitData.shoesId) || null,
      };

      return mappedOutfit;
    } catch (err) {
      console.error('Failed to generate outfit:', err);
      throw err;
    }
  }, [items, weather, profile]);

  // Tool: Add Item to Wardrobe
  const addToWardrobe = useCallback(async (file: File, metadata?: Partial<ClosetItem>) => {
    try {
      const imageUrl = await uploadImage(file);
      
      await addItem({
        name: metadata?.name || 'New Item',
        type: metadata?.type || 'Top',
        color: metadata?.color || 'White',
        occasion: metadata?.occasion || 'Casual',
        mood: metadata?.mood || 'Classic',
        price: metadata?.price || 0,
        brand: metadata?.brand || '',
        image_url: imageUrl,
      });

      return true;
    } catch (err) {
      console.error('Failed to add item:', err);
      throw err;
    }
  }, [addItem, uploadImage]);

  // Tool: Search Wardrobe
  const searchWardrobe = useCallback((query: string) => {
    const lowerQuery = query.toLowerCase();
    return items.filter(item => 
      item.name?.toLowerCase().includes(lowerQuery) ||
      item.type?.toLowerCase().includes(lowerQuery) ||
      item.color?.toLowerCase().includes(lowerQuery) ||
      item.occasion?.toLowerCase().includes(lowerQuery) ||
      item.mood?.toLowerCase().includes(lowerQuery) ||
      item.brand?.toLowerCase().includes(lowerQuery)
    );
  }, [items]);

  // Tool: Delete Item
  const removeFromWardrobe = useCallback(async (itemId: string) => {
    try {
      await deleteItem(itemId);
      return true;
    } catch (err) {
      console.error('Failed to delete item:', err);
      throw err;
    }
  }, [deleteItem]);

  // Tool: Share Outfit
  const shareOutfit = useCallback(async (outfit: any) => {
    try {
      await createPost({
        top: outfit.top,
        bottom: outfit.bottom,
        shoes: outfit.shoes,
        description: outfit.description || 'Styled by AURA',
        score: outfit.score || 85,
        styleTip: outfit.styleTip || '',
      });
      return true;
    } catch (err) {
      console.error('Failed to share outfit:', err);
      throw err;
    }
  }, [createPost]);

  // Main chat function with STREAMING support
  const sendMessage = useCallback(async (content: string) => {
    const startTime = Date.now();
    
    // Add user message
    addMessage({ role: 'user', content });
    
    // Add streaming message placeholder
    const loadingId = addMessage({ 
      role: 'assistant', 
      content: '', 
      isLoading: true,
      isStreaming: true,
    });

    setIsProcessing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        updateMessage(loadingId, {
          content: "Please sign in to continue our conversation.",
          isLoading: false,
          isStreaming: false,
        });
        return;
      }

      const sessionId = getSessionId();
      
      // Create abort controller for potential cancellation
      abortControllerRef.current = new AbortController();

      // Try streaming first
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/aura-agent`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: content,
            sessionId,
            stream: true,
            context: {
              wardrobe: items.slice(0, 50).map(i => ({
                id: i.id,
                name: i.name,
                type: i.type,
                color: i.color,
                occasion: i.occasion,
                mood: i.mood,
                brand: i.brand,
                price: i.price,
              })),
              weather,
              preferences: profile?.style_preferences,
              profile: {
                displayName: profile?.display_name,
                auraLevel: profile?.aura_level,
                isPremium: profile?.is_premium,
              },
            },
            conversationHistory: messages.slice(-10).map(m => ({
              role: m.role,
              content: m.content,
            })),
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to connect');
      }

      const contentType = response.headers.get('content-type');
      
      // Handle streaming response
      if (contentType?.includes('text/event-stream') && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let actions: AgentAction[] = [];

        updateMessage(loadingId, { isLoading: false });

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(line => line.startsWith('data: '));

          for (const line of lines) {
            const data = line.replace('data: ', '').trim();
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              
              if (parsed.done) {
                actions = parsed.actions || [];
              } else if (parsed.content) {
                fullContent += parsed.content;
                updateMessage(loadingId, { content: fullContent });
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }

        // Finalize message
        const durationMs = Date.now() - startTime;
        const interactionId = await logInteraction(
          'agent_chat',
          { message: content } as any,
          { response: fullContent, actions } as any,
          { wardrobeSize: items.length } as any,
          durationMs
        );

        updateMessage(loadingId, {
          content: fullContent || "I'm here to help with your style needs.",
          isStreaming: false,
          actions,
          interactionId,
        });

      } else {
        // Handle non-streaming (JSON) response
        const agentResponse = await response.json();
        
        if (agentResponse.insufficientCredits) {
          updateMessage(loadingId, {
            content: agentResponse.message,
            isLoading: false,
            isStreaming: false,
            actions: agentResponse.actions,
          });
          return;
        }

        const durationMs = Date.now() - startTime;
        const interactionId = await logInteraction(
          'agent_chat',
          { message: content } as any,
          { response: agentResponse.message, actions: agentResponse.actions } as any,
          { wardrobeSize: items.length } as any,
          durationMs
        );

        updateMessage(loadingId, {
          content: agentResponse.message || "I'm here to help with your style needs.",
          isLoading: false,
          isStreaming: false,
          actions: agentResponse.actions,
          display: agentResponse.display,
          data: agentResponse.data,
          interactionId,
        });
      }

    } catch (err: any) {
      if (err.name === 'AbortError') {
        updateMessage(loadingId, {
          content: "Message cancelled.",
          isLoading: false,
          isStreaming: false,
        });
        return;
      }
      
      console.error('Agent error:', err);
      updateMessage(loadingId, {
        content: "I'm having a moment. Let's try that again.",
        isLoading: false,
        isStreaming: false,
        actions: [{ type: 'retry', label: 'Try again' }],
      });
    } finally {
      setIsProcessing(false);
      abortControllerRef.current = null;
      scrollToBottom();
    }
  }, [addMessage, updateMessage, items, weather, profile, messages, scrollToBottom, logInteraction, getSessionId]);

  // Cancel streaming
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Execute agent action
  const executeAction = useCallback(async (action: AgentAction) => {
    switch (action.type) {
      case 'generate_outfit':
        const loadingId = addMessage({ 
          role: 'assistant', 
          content: 'Creating your perfect look...', 
          isLoading: true 
        });
        try {
          const outfit = await generateOutfit(action.params);
          updateMessage(loadingId, {
            content: outfit.styleTip || "Here's a curated look for you.",
            isLoading: false,
            display: 'outfit',
            data: outfit,
            actions: [
              { type: 'share_outfit', label: 'Share', params: { outfit } },
              { type: 'generate_outfit', label: 'Another' },
            ],
          });
        } catch (err) {
          updateMessage(loadingId, {
            content: "Add more items to your archive to generate outfits.",
            isLoading: false,
          });
        }
        break;

      case 'open_camera':
        addMessage({
          role: 'assistant',
          content: 'Ready to capture. Point at your clothing item.',
          display: 'camera',
        });
        break;

      case 'browse_wardrobe':
        addMessage({
          role: 'assistant',
          content: `Your archive contains ${items.length} pieces. Here's an overview.`,
          display: 'wardrobe',
          data: { items: items.slice(0, 8) },
          actions: [
            { type: 'open_camera', label: 'Add item' },
            { type: 'search_wardrobe', label: 'Search' },
          ],
        });
        break;

      case 'share_outfit':
        try {
          await shareOutfit(action.params?.outfit);
          toast.success('Look shared to the Syndicate!');
          addMessage({
            role: 'assistant',
            content: "Your look is live! The Syndicate awaits.",
            actions: [{ type: 'generate_outfit', label: 'Create another' }],
          });
        } catch (err) {
          toast.error('Could not share look');
        }
        break;

      case 'add_item':
        if (action.params?.file) {
          try {
            await addToWardrobe(action.params.file, action.params.metadata);
            toast.success('Added to your archive');
            addMessage({
              role: 'assistant',
              content: "Item added to your digital archive. Your wardrobe just got better.",
              actions: [
                { type: 'open_camera', label: 'Add another' },
                { type: 'generate_outfit', label: 'Style me' },
              ],
            });
          } catch (err) {
            toast.error('Could not add item');
          }
        }
        break;

      case 'delete_item':
        if (action.params?.itemId) {
          try {
            await removeFromWardrobe(action.params.itemId);
            toast.success('Removed from archive');
          } catch (err) {
            toast.error('Could not remove item');
          }
        }
        break;

      case 'retry':
        const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
        if (lastUserMsg) {
          await sendMessage(lastUserMsg.content);
        }
        break;

      default:
        console.log('Unknown action:', action.type);
    }
  }, [addMessage, updateMessage, generateOutfit, shareOutfit, addToWardrobe, removeFromWardrobe, items, messages, sendMessage]);

  return {
    messages,
    isProcessing,
    sendMessage,
    executeAction,
    addMessage,
    updateMessage,
    messagesEndRef,
    cancelStreaming,
    tools: {
      generateOutfit,
      addToWardrobe,
      searchWardrobe,
      removeFromWardrobe,
      shareOutfit,
    },
    context: {
      wardrobe: items,
      weather,
      profile,
    },
  };
}
